import React, { useEffect, useId, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";

import { useToast } from "../../components/ui";
import type { CSITipaltiStaging } from "../../types/CSITypes";
import agent from "../../utils/axios";
import { isReviewResultValue, REVIEW_FORM_OPTIONS } from "./reviewResults";
import { applyReview, buildReviewPayload, ReviewChange } from "./stagingData";
import { useCurrentUserName } from "./useCurrentUserName";

export interface ReviewFormProps {
  row: CSITipaltiStaging;
  /** Called with the row as it reads after a successful save. */
  onSaved: (row: CSITipaltiStaging) => void;
}

/** The review fields the form edits, as saved on a row. */
type SavedReview = Pick<
  CSITipaltiStaging,
  "memo" | "reviewedResults" | "isActive"
>;

const fromRow = (row: SavedReview): ReviewChange => ({
  memo: row.memo ?? "",
  reviewedResults: row.reviewedResults ?? "",
  isActive: row.isActive,
});

/**
 * Review result, note and Active for one row, saved with the same POST the screen has
 * always used: the FULL row with those fields replaced. The row object itself is never
 * modified; the saved version goes to `onSaved`.
 */
export function ReviewForm({ row, onSaved }: ReviewFormProps) {
  const toast = useToast();
  const userName = useCurrentUserName();
  const [draft, setDraft] = useState<ReviewChange>(() => fromRow(row));
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  const fieldId = `review-${useId()}`;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Start again from the row only when it is another row or its saved review changed (a
  // save here or elsewhere). A parent re-render that passes a new object with the same
  // values (a list refresh, another row's save) keeps an unsaved draft.
  const { id: rowId, memo, reviewedResults, isActive } = row;
  useEffect(() => {
    setDraft(fromRow({ memo, reviewedResults, isActive }));
  }, [rowId, memo, reviewedResults, isActive]);

  const saved = fromRow(row);
  const dirty =
    draft.memo !== saved.memo ||
    draft.reviewedResults !== saved.reviewedResults ||
    draft.isActive !== saved.isActive;

  // Keep a stored value the list doesn't know, so it shows and saves unchanged.
  const options =
    saved.reviewedResults && !isReviewResultValue(saved.reviewedResults)
      ? [
          ...REVIEW_FORM_OPTIONS,
          { value: saved.reviewedResults, label: saved.reviewedResults },
        ]
      : REVIEW_FORM_OPTIONS;

  const handleSave = async () => {
    const stamp = { at: new Date().toISOString(), by: userName };
    setSaving(true);
    try {
      await agent.CSITipalti.updateCSITipalti(
        row.id,
        buildReviewPayload(row, draft, stamp)
      );
      onSaved(applyReview(row, draft, stamp));
      toast.success(`Review saved for ${row.csiName || "this payee"}`);
    } catch {
      toast.error("Couldn't save the review. Try again.");
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      aria-label={`Review for ${row.csiName || "this payee"}`}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        if (dirty && !saving) handleSave();
      }}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "220px 1fr auto" },
        alignItems: "start",
        gap: 3,
        "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
      }}
    >
      <Box sx={{ display: "grid", gap: 1 }}>
        <TextField
          select
          id={`${fieldId}-result`}
          label="Review result"
          size="small"
          value={draft.reviewedResults}
          onChange={(e) =>
            setDraft((d) => ({ ...d, reviewedResults: e.target.value }))
          }
          disabled={saving}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          {options.map((option) => (
            <MenuItem key={option.value || "none"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={draft.isActive}
              onChange={(e) =>
                setDraft((d) => ({ ...d, isActive: e.target.checked }))
              }
              disabled={saving}
            />
          }
          label="Active"
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8125rem" } }}
        />
      </Box>
      <TextField
        id={`${fieldId}-note`}
        label="Note"
        size="small"
        multiline
        minRows={2}
        maxRows={6}
        value={draft.memo}
        onChange={(e) => setDraft((d) => ({ ...d, memo: e.target.value }))}
        disabled={saving}
        InputLabelProps={{ shrink: true }}
        placeholder="What was decided, and why"
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "row", md: "column" },
          alignItems: { xs: "center", md: "stretch" },
          gap: 1,
        }}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={!dirty || saving}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Save review
        </Button>
        {dirty && !saving && (
          <Button
            type="button"
            size="small"
            onClick={() => setDraft(fromRow(row))}
          >
            Discard changes
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default ReviewForm;
